// src/features/account/components/CreditsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Coins, Plus, ArrowUpRight, ArrowDownRight, AlertCircle, CheckCircle, RefreshCw, ChevronDown, ChevronRight, Zap } from 'lucide-react';
import { useWorkOS } from '../../workos/context/WorkOSContext';
import { useSearchParams } from 'react-router-dom';

const DEPOSIT_PACKAGES = [
  { id: 'credits_10', amount: 10, cents: 1000, label: '$10' },
  { id: 'credits_25', amount: 25, cents: 2500, label: '$25', popular: true },
  { id: 'credits_50', amount: 50, cents: 5000, label: '$50' }
];

const CreditsTab = () => {
  const { user, getValidAccessToken } = useWorkOS();
  const [searchParams] = useSearchParams();
  const [customAmount, setCustomAmount] = useState('');

  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [aiUsageGroups, setAiUsageGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [isLoadingAI, setIsLoadingAI] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check for deposit success/cancel from URL params
  useEffect(() => {
    const depositStatus = searchParams.get('deposit');
    if (depositStatus === 'success') {
      setSuccessMessage('Funds added successfully!');
      window.history.replaceState({}, '', '/account?tab=credits');
    } else if (depositStatus === 'cancelled') {
      setError('Purchase was cancelled.');
      window.history.replaceState({}, '', '/account?tab=credits');
    }
  }, [searchParams]);

  // Fetch balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!user?.accessToken) return;

      try {
        setIsLoading(true);
        const accessToken = await getValidAccessToken();

        const response = await fetch('/.netlify/functions/credits-balance', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch balance');
        }

        const data = await response.json();
        setBalance(data.balance_cents);
      } catch (err) {
        console.error('Error fetching balance:', err);
        setError('Failed to load balance');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalance();
  }, [user, getValidAccessToken]);

  // Fetch transaction history
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user?.accessToken) return;

      try {
        setIsLoadingTransactions(true);
        const accessToken = await getValidAccessToken();

        const response = await fetch('/.netlify/functions/credits-history?limit=10', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch transaction history');
        }

        const data = await response.json();
        setTransactions(data.transactions || []);
      } catch (err) {
        console.error('Error fetching transactions:', err);
      } finally {
        setIsLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [user, getValidAccessToken]);

  // Fetch AI usage and group by workflow+day
  useEffect(() => {
    const fetchAIUsage = async () => {
      if (!user?.accessToken) return;

      try {
        setIsLoadingAI(true);
        const accessToken = await getValidAccessToken();

        const response = await fetch('/.netlify/functions/ai-usage?days=30', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch AI usage');
        }

        const data = await response.json();
        
        // Group logs by workflow_id + date
        const groups = {};
        (data.logs || []).forEach(log => {
          const date = new Date(log.created_at).toISOString().split('T')[0];
          const workflow = log.workflow_id || 'Unknown Workflow';
          const key = `${workflow}|${date}`;
          
          if (!groups[key]) {
            groups[key] = {
              workflow,
              date,
              totalCredits: 0,
              totalTokens: 0,
              requests: [],
            };
          }
          
          groups[key].totalCredits += log.credits_charged || 0;
          groups[key].totalTokens += (log.input_tokens || 0) + (log.output_tokens || 0);
          groups[key].requests.push(log);
        });

        // Convert to array and sort by date descending
        const groupArray = Object.values(groups).sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        
        setAiUsageGroups(groupArray);
      } catch (err) {
        console.error('Error fetching AI usage:', err);
      } finally {
        setIsLoadingAI(false);
      }
    };

    fetchAIUsage();
  }, [user, getValidAccessToken]);

  const handlePurchase = async (packageId, customCents = null) => {
    try {
      setIsPurchasing(true);
      setError(null);

      const accessToken = await getValidAccessToken();

      const bodyData = customCents
        ? { custom_amount_cents: customCents }
        : { package_id: packageId };

      const response = await fetch('/.netlify/functions/credits-deposit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(bodyData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate purchase');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;

    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.message || 'Failed to start purchase. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isThisYear = date.getFullYear() === now.getFullYear();

    if (isToday) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: isThisYear ? undefined : 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === now.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCredits = (amount) => {
    if (!amount) return '0.00';
    // Convert credits to dollars (10 credits = $1)
    const dollars = amount / 10;
    if (dollars < 0.01) return dollars.toFixed(4);
    return dollars.toFixed(2);
  };

  const formatTokens = (tokens) => {
    if (!tokens) return '0';
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toString();
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Refresh balance and transactions
  const handleRefresh = useCallback(async () => {
    if (!user?.accessToken || isRefreshing) return;

    setIsRefreshing(true);
    setError(null);

    try {
      const accessToken = await getValidAccessToken();

      const [balanceRes, transactionsRes, aiRes] = await Promise.all([
        fetch('/.netlify/functions/credits-balance', {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }),
        fetch('/.netlify/functions/credits-history?limit=10', {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }),
        fetch('/.netlify/functions/ai-usage?days=30', {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        })
      ]);

      if (balanceRes.ok) {
        const balanceData = await balanceRes.json();
        setBalance(balanceData.balance_cents);
      }

      if (transactionsRes.ok) {
        const transactionsData = await transactionsRes.json();
        setTransactions(transactionsData.transactions || []);
      }

      if (aiRes.ok) {
        const aiData = await aiRes.json();
        // Re-group AI logs
        const groups = {};
        (aiData.logs || []).forEach(log => {
          const date = new Date(log.created_at).toISOString().split('T')[0];
          const workflow = log.workflow_id || 'Unknown Workflow';
          const key = `${workflow}|${date}`;
          
          if (!groups[key]) {
            groups[key] = {
              workflow,
              date,
              totalCredits: 0,
              totalTokens: 0,
              requests: [],
            };
          }
          
          groups[key].totalCredits += log.credits_charged || 0;
          groups[key].totalTokens += (log.input_tokens || 0) + (log.output_tokens || 0);
          groups[key].requests.push(log);
        });

        setAiUsageGroups(Object.values(groups).sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        ));
      }
    } catch (err) {
      console.error('Error refreshing:', err);
      setError('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  }, [user, getValidAccessToken, isRefreshing]);

  if (isLoading) {
    return (
      <div className="bg-zinc-900 rounded-lg p-6">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center text-green-400 bg-green-500/10 p-4 rounded-lg">
          <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center text-red-400 bg-red-500/10 p-4 rounded-lg">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Balance Card */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Current Balance</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh balance"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <Coins className="w-6 h-6 text-yellow-500" />
          </div>
        </div>
        <div className="text-4xl font-bold text-white">
          ${((balance || 0) / 100).toFixed(2)}
        </div>
      </div>

      {/* Add Funds */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Add Funds</h3>
        <p className="text-zinc-400 text-sm mb-4">
          Your balance is used to run AI workflows in the cloud.
        </p>

        <div className="grid grid-cols-3 gap-3">
          {DEPOSIT_PACKAGES.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => handlePurchase(pkg.id)}
              disabled={isPurchasing}
              className={`relative p-4 rounded-lg border-2 transition-all
                ${pkg.popular
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-zinc-600 hover:border-zinc-500 bg-zinc-700/50'}
                disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {pkg.popular && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                  Popular
                </span>
              )}
              <div className="text-2xl font-bold text-white">{pkg.label}</div>
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <div className="mt-4 flex gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
            <input
              type="number"
              min="1"
              step="1"
              placeholder="Custom amount"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              className="w-full pl-7 pr-3 py-2.5 bg-zinc-700 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <button
            onClick={() => {
              const cents = Math.round(parseFloat(customAmount) * 100);
              if (cents >= 100) handlePurchase(null, cents);
              else setError('Minimum amount is $1.00');
            }}
            disabled={isPurchasing || !customAmount}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Add Funds
          </button>
        </div>

        {isPurchasing && (
          <div className="mt-4 flex items-center justify-center text-zinc-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Redirecting to checkout...
          </div>
        )}
      </div>

      {/* AI Usage - Grouped by workflow+day */}
      {!isLoadingAI && aiUsageGroups.length > 0 && (
        <div className="bg-zinc-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-medium text-white">AI Usage</h3>
            <span className="text-zinc-500 text-sm">(last 30 days)</span>
          </div>

          <div className="space-y-2">
            {aiUsageGroups.map((group) => {
              const key = `${group.workflow}|${group.date}`;
              const isExpanded = expandedGroups[key];
              
              return (
                <div key={key} className="border border-zinc-700 rounded-lg overflow-hidden">
                  {/* Group header - clickable */}
                  <button
                    onClick={() => toggleGroup(key)}
                    className="w-full flex items-center justify-between p-3 hover:bg-zinc-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-zinc-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                      )}
                      <div className="p-2 rounded-full bg-cyan-500/20 text-cyan-400">
                        <Zap className="w-4 h-4" />
                      </div>
                      <div className="text-left">
                        <div className="text-white text-sm">AI: {group.workflow}</div>
                        <div className="text-xs text-zinc-500">
                          {formatDateShort(group.date)} · {group.requests.length} request{group.requests.length !== 1 ? 's' : ''} · {formatTokens(group.totalTokens)} tokens
                        </div>
                      </div>
                    </div>
                    <div className="text-cyan-400 font-medium">
                      -${formatCredits(group.totalCredits)}
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-zinc-700 bg-zinc-900/50">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-zinc-500 uppercase">
                            <th className="px-4 py-2 text-left">Time</th>
                            <th className="px-4 py-2 text-left">Model</th>
                            <th className="px-4 py-2 text-right">Tokens</th>
                            <th className="px-4 py-2 text-right">Cost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                          {group.requests.map((req) => (
                            <tr key={req.id} className={!req.success ? 'opacity-50' : ''}>
                              <td className="px-4 py-2 text-zinc-400">
                                {new Date(req.created_at).toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="px-4 py-2 text-zinc-300">
                                {req.model.split('/').pop()}
                                {!req.success && <span className="ml-2 text-red-400 text-xs">failed</span>}
                              </td>
                              <td className="px-4 py-2 text-right text-zinc-400">
                                {formatTokens((req.input_tokens || 0) + (req.output_tokens || 0))}
                              </td>
                              <td className="px-4 py-2 text-right text-cyan-400">
                                ${formatCredits(req.credits_charged)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Transaction History (deposits & workflow purchases) */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Recent Transactions</h3>

        {isLoadingTransactions ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center text-zinc-400 py-4">
            No transactions yet
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="flex items-center justify-between py-3 border-b border-zinc-700 last:border-0"
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${
                    tx.amount_cents > 0
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {tx.amount_cents > 0
                      ? <ArrowDownRight className="w-4 h-4" />
                      : <ArrowUpRight className="w-4 h-4" />
                    }
                  </div>
                  <div>
                    <div className="text-white text-sm">{tx.description}</div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500">{formatDate(tx.created_at)}</span>
                      {tx.listing && (
                        <span className="text-zinc-600">• {tx.listing.title}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`font-medium ${
                  tx.amount_cents > 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {tx.amount_cents > 0 ? '+' : ''}{tx.amount_dollars}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditsTab;
